import { InboxOutlined } from '@ant-design/icons';
import DetailPageHeader from '@components/detail-page-header';
import Link from '@components/link';
import {
  Alert,
  Button,
  Progress,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  confirmBillingMatch,
  importPacketaCsv,
  importPacketaCsvStart,
} from '../reducers/packets/reducer';
import { type IImportPacketaCsvPendingMatch } from '../reducers/packets/types';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;
const { Dragger } = Upload;

function PendingMatchRow({
  match,
  isConfirming,
}: {
  match: IImportPacketaCsvPendingMatch;
  isConfirming: boolean;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const [selectedPacketId, setSelectedPacketId] = useState<number | null>(null);

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Text>
        <Text strong>Order:</Text>{' '}
        <Link
          to={`/order/${match.orderUuid}`}
          label={match.orderUuid}
          openInNewTab
        />
        &nbsp;
        <Text strong>Barcode CSV:</Text> {match.barcode} &nbsp;
        <Text strong>Billed:</Text> {match.billedPrice} {match.billedCurrency}{' '}
        &nbsp;
        <Text strong>Date:</Text> {match.billedDate.slice(0, 10)}
      </Text>
      <Text type="secondary">
        Barcode not matched — select the correct packet from the list below.
      </Text>
      <Space>
        <Select
          style={{ minWidth: '17.5rem' }}
          placeholder="Select packet"
          value={selectedPacketId}
          onChange={(v): void => setSelectedPacketId(v)}
          options={match.availablePackets.map((p) => ({
            value: p.id,
            label: `#${p.id} — ${p.thirdPartyId}${p.barcode ? ` (${p.barcode})` : ''}`,
          }))}
        />
        <Button
          type="primary"
          disabled={selectedPacketId === null || isConfirming}
          loading={isConfirming}
          onClick={(): void => {
            if (selectedPacketId !== null) {
              dispatch(
                confirmBillingMatch(
                  selectedPacketId,
                  match.billedPrice,
                  match.billedCurrency,
                  match.billedDate,
                ),
              );
            }
          }}
        >
          Confirm
        </Button>
      </Space>
    </Space>
  );
}

function PacketsVendorPacketaContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const packetsImportCurrent = useSelector(
    (s: RootState) => s.Packets.importCurrent,
  );
  const packetsImportTotal = useSelector(
    (s: RootState) => s.Packets.importTotal,
  );
  const packetsInProcess = useSelector((s: RootState) => s.Packets.inProcess);
  const packetsImportResult = useSelector(
    (s: RootState) => s.Packets.importResult,
  );
  const packetsConfirmingPacketIds = useSelector(
    (s: RootState) => s.Packets.confirmingPacketIds,
  );

  const queueRef = useRef<string[]>([]);

  const handleUpload = (file: File, allFiles: File[]): boolean => {
    if (file !== allFiles[allFiles.length - 1]) {
      return false;
    }

    const readers = allFiles.map(
      (f) =>
        new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e): void =>
            resolve((e.target?.result as string) ?? '');
          reader.readAsText(f, 'utf-8');
        }),
    );

    Promise.all(readers).then((csvContents) => {
      const valid = csvContents.filter(Boolean);
      queueRef.current = valid.slice(1);
      dispatch(importPacketaCsvStart(valid.length));
      dispatch(importPacketaCsv(valid[0]));
    });

    return false;
  };

  useEffect(() => {
    if (queueRef.current.length === 0) return;
    const next = queueRef.current.shift();
    if (next) dispatch(importPacketaCsv(next));
  }, [packetsImportCurrent]);

  const percent =
    packetsImportTotal && packetsImportTotal > 0
      ? Math.round(((packetsImportCurrent ?? 0) / packetsImportTotal) * 100)
      : 0;

  const pendingMatches = packetsImportResult?.pendingMatches ?? [];

  return (
    <>
      <DetailPageHeader
        title="Packeta — import CSV billing"
        info={
          <Text type="secondary">
            Upload one or more Packeta billing CSV exports. Only rows belonging
            to ROPERE will be processed. Shipping costs will be matched to
            existing packets and converted to the order currency using
            historical exchange rates.
          </Text>
        }
      />

      <Space size={SPACING.xl} orientation="vertical" style={{ width: '100%' }}>
        <Dragger
          name="file"
          accept=".csv"
          multiple={true}
          beforeUpload={handleUpload}
          showUploadList={false}
          disabled={packetsInProcess}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag CSV file(s) to this area
          </p>
          <p className="ant-upload-hint">
            Packeta billing export (.csv, semicolon-delimited). Multiple files
            allowed.
          </p>
        </Dragger>

        {packetsInProcess && (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Text>
              Importing… {packetsImportCurrent ?? 0} /{' '}
              {packetsImportTotal ?? '?'} files
            </Text>
            <Progress percent={percent} status="active" />
          </Space>
        )}

        {packetsImportResult && !packetsInProcess && (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Alert
              type={
                packetsImportResult.errors.length > 0 ||
                pendingMatches.length > 0
                  ? 'warning'
                  : 'success'
              }
              title={`Import complete — ${packetsImportResult.processed} processed, ${packetsImportResult.skipped} skipped${pendingMatches.length > 0 ? `, ${pendingMatches.length} require manual pairing` : ''}`}
              showIcon
            />
            {packetsImportResult.errors.length > 0 && (
              <div>
                <Text strong>Errors</Text>
                {packetsImportResult.errors.map((err, i) => (
                  <div key={i}>
                    <Text type="danger">{err}</Text>
                  </div>
                ))}
              </div>
            )}
            {pendingMatches.length > 0 && (
              <div>
                <Text strong>Manual pairing required</Text>
                {pendingMatches.map((match) => (
                  <div key={`${match.orderUuidCsv}-${match.barcode}`}>
                    <PendingMatchRow
                      match={match}
                      isConfirming={match.availablePackets.some((p) =>
                        packetsConfirmingPacketIds.includes(p.id),
                      )}
                    />
                  </div>
                ))}
              </div>
            )}
          </Space>
        )}
      </Space>
    </>
  );
}

export default PacketsVendorPacketaContainer;
